import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import type { QueueViewKey } from '../types/encounterWork';

import type { WorkbenchQueueEntry } from './worklistProvenance';

/**
 * The run behind "Work this queue" and a card row: the WHOLE queue, read a
 * page at a time into one cache entry (`store/queue-run-api.ts`).
 *
 * Home is an entry into the queue, not into the five rows the card shows
 * (Alfred's test of 2026-09-04: a run that ended at the fifth row was the
 * card, not the queue). So the queue is read again behind the card, in queue
 * order, and every row the workbench can hold becomes a step. The click
 * leaves on the FIRST page; the rest land behind the biller and the rail
 * grows as they do. These are the pure rules of that read: what a step is,
 * how a page joins the run, and when the run stops.
 */

/** Rows per page for the run read. */
export const QUEUE_RUN_PAGE_LIMIT = 100;

/**
 * The run stops after ten pages: a thousand entries. A queue longer than
 * that is not something a biller works through in one sitting, and a rail
 * that promised "1 of 4,000" would be honest and useless. `viewCount` still
 * carries the server's whole count, so the rail can admit the run is shorter
 * than the queue, the same way it does when fewer rows were loaded.
 */
export const QUEUE_RUN_MAX_PAGES = 10;
export const QUEUE_RUN_MAX_ENTRIES = QUEUE_RUN_PAGE_LIMIT * QUEUE_RUN_MAX_PAGES;

/** The two facts a step needs. Any row shape that carries them will do. */
export interface QueueRunRow {
  claim_id: string;
  encounter_id?: string | null;
}

/** One page of the claim-work read, as much of it as the run needs. */
export interface QueueRunPage {
  counts?: { work_view_counts?: Partial<Record<string, number>> } | null;
  items?: readonly QueueRunRow[] | null;
  next_cursor?: string | null;
}

/** What a page read leaves behind when it fails: the base query's own error. */
export type QueueRunPageError = FetchBaseQueryError | SerializedError;

/**
 * The run as the cache holds it.
 *
 * APPEND-ONLY. A page only ever adds steps after the last one, so a position
 * derived against the run stays true while it grows — the frozen-run rule of
 * GDN-3322, kept: nothing here is ever re-read, renumbered or dropped.
 */
export interface QueueRun {
  /** Every step, once: a claim the server lists twice is one claim. */
  entries: WorkbenchQueueEntry[];
  /** The server's count for the queue, from the first page. Null when that
   *  page carried no count for this key. */
  viewCount: number | null;
  /** The page to read next. Null once there is nothing left to read. */
  nextCursor: string | null;
  /** Cursors already followed. A cursor the server hands out twice names a
   *  page already read, and following it would list the same claims again. */
  cursors: string[];
  pagesRead: number;
  /** Every page the run will hold has been read: the queue ran out, the cap
   *  was reached, or the server repeated itself. */
  complete: boolean;
  /** A page failed after its retries. What loaded stays, and a retry reads
   *  on from `nextCursor` rather than starting over. */
  error: QueueRunPageError | null;
}

/** The run before its first page. */
export const EMPTY_QUEUE_RUN: QueueRun = {
  complete: false,
  cursors: [],
  entries: [],
  error: null,
  nextCursor: null,
  pagesRead: 0,
  viewCount: null,
};

/**
 * The rows a run can hold: the one rule the card already applies. A claim
 * whose lines name no encounter routes to the legacy view, which has no rail
 * and no Next, so it is shown but is never a step.
 */
export const queueRunEntries = (rows: readonly QueueRunRow[]): WorkbenchQueueEntry[] =>
  rows.flatMap(row =>
    row.encounter_id ? [{ claimId: row.claim_id, encounterId: row.encounter_id }] : [],
  );

/** The server's count for this queue on a page, or null when it carried none. */
const pageViewCount = (page: QueueRunPage, queue: QueueViewKey): number | null => {
  const count = page.counts?.work_view_counts?.[queue];
  return typeof count === 'number' ? count : null;
};

/**
 * The run after one more page.
 *
 * `cursor` is what this page was read with — null for the first. Steps are
 * appended in page order and a claim already in the run keeps its place:
 * the workbench finds a claim's position by its FIRST match, so a duplicate
 * would pin every later step to the wrong row. The run is complete once the
 * server has no next page, has repeated a cursor, or the cap is reached.
 */
export function appendQueueRunPage(
  run: QueueRun,
  queue: QueueViewKey,
  page: QueueRunPage,
  cursor: string | null,
): QueueRun {
  const listed = new Set(run.entries.map(entry => entry.claimId));
  const entries = [...run.entries];
  for (const entry of queueRunEntries(page.items ?? [])) {
    if (listed.has(entry.claimId)) continue;
    listed.add(entry.claimId);
    entries.push(entry);
  }
  const cursors = cursor ? [...run.cursors, cursor] : run.cursors;
  const pagesRead = run.pagesRead + 1;
  const next = page.next_cursor ?? null;
  const complete =
    !next ||
    cursors.includes(next) ||
    pagesRead >= QUEUE_RUN_MAX_PAGES ||
    entries.length >= QUEUE_RUN_MAX_ENTRIES;
  return {
    complete,
    cursors,
    entries: entries.slice(0, QUEUE_RUN_MAX_ENTRIES),
    error: null,
    nextCursor: complete ? null : next,
    pagesRead,
    viewCount: run.pagesRead === 0 ? pageViewCount(page, queue) : run.viewCount,
  };
}
